import React, { useCallback, useEffect, useState } from 'react';
import { useCategories } from '@hooks';
import { IconSelect } from '../atoms';
import { CreateEquipmentData, Equipment, UpdateEquipmentData } from '../../types/equipment';
import { ImageUploader, UploadImage } from '../molecules';

interface EquipmentFormProps {
  equipment?: Equipment | null;
  onSave: (data: CreateEquipmentData | UpdateEquipmentData) => void;
  onCancel: () => void;
}

const EquipmentForm: React.FC<EquipmentFormProps> = ({ equipment, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: equipment?.name || '',
    description: equipment?.description || '',
    pricePerDay: equipment?.pricePerDay || 0,
    categoryId: equipment?.categoryId || '',
    features: equipment?.features?.join('\n') || '',
    images: equipment?.images?.sort((a, b) => (a.position || 0) - (b.position || 0))
    .map(img => { return { url: img.url, id: img.id } as UploadImage }) || [],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      features: formData.features.split('\n').filter(f => f.trim()),
      images: formData.images,
      ...(equipment && { id: equipment.id })
    });
  };

  const [images, setImages] = useState<UploadImage[]>(
    equipment?.images.map(img => ({
      id: img.id,
      url: img.url,
      status: 'done',
      attempts: 0,
    })) || []
  )

  useEffect(() => {
    setFormData({
      name: equipment?.name || '',
      description: equipment?.description || '',
      pricePerDay: equipment?.pricePerDay || 0,
      categoryId: equipment?.categoryId || '',
      features: equipment?.features?.join('\n') || '',
      images: images,
    });
  }, [equipment, images]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onCancel]);

  const updateImage = (update: Partial<UploadImage> & { url: string }) => {
    setImages(cur => cur.map(i => (i.url === update.url ? { ...i, ...update } : i)));
  };

  const handleAddImage = useCallback((files: File[]) => {
    const uploadOneSim = (img: UploadImage) => {
      if (img.status === 'uploading' || img.status === 'done') return;
      updateImage({ url: img.url, status: 'uploading', attempts: (img.attempts ?? 0) + 1, progress: 0 });
      let prog = 0;
      const interval = setInterval(() => {
        prog += 10;
        if (prog >= 100) {
          clearInterval(interval);
          updateImage({ url: img.url, status: 'done', progress: 100 });
        } else {
          updateImage({ url: img.url, progress: prog });
        }
      }, 200);
    };

    const newImgs: UploadImage[] = files.map(file => ({
      url: URL.createObjectURL(file),
      file,
      status: 'pending',
      attempts: 0,
    }))
    setImages(cur => [...cur, ...newImgs])
    newImgs.forEach(uploadOneSim)
  }, [])

  const { categories, loading: loadingCategories } = useCategories();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-blue-500/80 bg-opacity-50 animate-in fade-in-0 duration-300"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-xl font-bold">
          {equipment ? 'Edit Equipment' : 'Add New Equipment'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <ImageUploader
            images={images}
            onAdd={handleAddImage}
            onReorder={(images => setImages(images))}
            onRemove={(image: UploadImage) => {setImages(images.filter(img => img.url !== image.url))}}
            onRetry={() => { }}
            maxCount={5}
            acceptTypes={['image/jpeg', 'image/png']}
          />
          <div>
            <label htmlFor="equipment-name" className="block mb-1 text-sm font-medium text-gray-700">Nombre</label>
            <input
              id="equipment-name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="equipment-price" className="block mb-1 text-sm font-medium text-gray-700">Precio por día (€)</label>
              <input
                id="equipment-price"
                type="number"
                required
                value={formData.pricePerDay}
                onChange={(e) => setFormData({ ...formData, pricePerDay: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="equipment-category" className="block mb-1 text-sm font-medium text-gray-700">Categoría</label>
              {loadingCategories ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-400">Cargando...</div>
              ) : (
                <IconSelect
                  id="equipment-category"
                  value={formData.categoryId}
                  options={categories.map(cat => ({ value: cat.id, label: cat.name, icon: cat.icon }))}
                  onChange={catId => setFormData({ ...formData, categoryId: catId })}
                  className="w-full"
                />
              )}
            </div>
          </div>
          <div>
            <label htmlFor="equipment-description" className="block mb-1 text-sm font-medium text-gray-700">Descripción</label>
            <textarea
              id="equipment-description"
              required
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label htmlFor="equipment-features" className="block mb-1 text-sm font-medium text-gray-700">Características (una por línea)</label>
            <textarea
              id="equipment-features"
              rows={4}
              value={formData.features}
              onChange={(e) => setFormData({ ...formData, features: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
            />
          </div>

          <div className="flex pt-4 space-x-3">
            <button
              type="submit"
              className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              {equipment ? 'Actualizar Equipo' : 'Agregar Equipo'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-300 rounded-lg hover:bg-gray-400"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EquipmentForm;
